import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getPaginatedExceededPartModelConstraints,
  getPaginatedPartModelConstraints,
  getPartModelConstraint,
  getPartModelConstraintsByMatch,
} from "@/api/resources/partModelConstraints/api";
import i18n from "@/providers/i18n";
import { type QueryPaginationFilters } from "@/utils/pagination";

import type { PartModelConstraint } from "@/supabase.types";

const entity = i18n.t("part-model-constraint");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("part-model-constraints");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const partModelConstraintsQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "partModelConstraints"],
  lists: () => [...partModelConstraintsQueryKeys.all(), "list"],
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...partModelConstraintsQueryKeys.lists(),
    "paginated",
    queryKeyPaginationFilters,
  ],
  listPaginatedExceeded: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...partModelConstraintsQueryKeys.lists(),
    "paginatedExceeded",
    queryKeyPaginationFilters,
  ],
  listByMatch: (match?: Partial<PartModelConstraint>) => [
    ...partModelConstraintsQueryKeys.lists(),
    "listByMatch",
    match,
  ],
  byId: (id?: string) => [...partModelConstraintsQueryKeys.all(), "byId", id],
};
export const partModelConstraintsQueries = {
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...partModelConstraintsQueryKeys.listPaginated(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedPartModelConstraints(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
      select: ({ data, count }) => ({ count, data }),
    }),
  listPaginatedExceeded: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...partModelConstraintsQueryKeys.listPaginatedExceeded(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedExceededPartModelConstraints(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
      select: ({ data, count }) => ({ count, data }),
    }),
  listByMatch: (match: Partial<PartModelConstraint> = {}) =>
    queryOptions({
      queryKey: [...partModelConstraintsQueryKeys.listByMatch(match)],
      queryFn: () => getPartModelConstraintsByMatch(match),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...partModelConstraintsQueryKeys.byId(id)],
      queryFn: id ? () => getPartModelConstraint({ match: { id } }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
